/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import StepContainer from '../StepContainer';

interface StepThreeProps {
  data: {
    name: string;
    whatsapp: string;
    referralCode: string | null;
  };
  onBack: () => void;
  onSubmitChildren: (data: any) => void;
  price: {
    total: string;
    cupom: any;
  };
  planoSelecionado: {
    name: string;
    price: number;
  };
}

const StepThree: React.FC<StepThreeProps> = ({ data, onBack, onSubmitChildren, planoSelecionado }) => {
  const [cep, setCep] = useState('');
  const [loading, setLoading] = useState(false);
  const [cepNotFound, setCepNotFound] = useState(false);
  const [manualEntryEnabled, setManualEntryEnabled] = useState(false);
  const [address, setAddress] = useState({
    rua: '',
    bairro: '',
    cidade: '',
    estado: '',
    numero: '',
    complemento: '',
    recebedor: '',
  });
  const [isEditing, setIsEditing] = useState(true);
  const [fieldErrors, setFieldErrors] = useState<{
    cep?: string;
    numero?: string;
  }>({});
  const [touched, setTouched] = useState<{
    cep: boolean;
    numero: boolean;
  }>({
    cep: false,
    numero: false,
  });
  const numeroRef = useRef<HTMLInputElement>(null);

  const fetchEndereco = async (cepLimpo: string) => {
    try {
      setLoading(true);
      setCepNotFound(false);
      setManualEntryEnabled(false);

      const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await response.json();

      if (data.erro) {
        return await fetchEnderecoViaOpenCEP(cepLimpo);
      }

      // Preenche com os dados disponíveis, mesmo que alguns campos estejam vazios
      setAddress((prev) => ({
        ...prev,
        rua: data.logradouro || '',
        bairro: data.bairro || '',
        cidade: data.localidade || '',
        estado: data.uf || '',
        numero: '',
        complemento: '',
      }));

      // Se campos importantes estão vazios, habilita entrada manual
      if (!data.logradouro || !data.bairro) {
        setManualEntryEnabled(true);
      }

      setTimeout(() => numeroRef.current?.focus(), 100);
    } catch {
      await fetchEnderecoViaOpenCEP(cepLimpo);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnderecoViaOpenCEP = async (cep: string) => {
    try {
      const response = await fetch(`https://opencep.com/v1/${cep}.json`);
      const data = await response.json();

      // Se não houver dados ou CEP inválido
      if (!data || (!data.logradouro && !data.localidade)) {
        // CEP não encontrado - habilitar campos imediatamente
        setCepNotFound(true);
        setManualEntryEnabled(true);
        setLoading(false);
        return;
      }

      // Preenche com os dados disponíveis, mesmo que alguns campos estejam vazios
      setAddress((prev) => ({
        ...prev,
        rua: data.logradouro || '',
        bairro: data.bairro || '',
        cidade: data.localidade || '',
        estado: data.uf || '',
        numero: '',
        complemento: '',
      }));

      // Se campos importantes estão vazios, habilita entrada manual
      if (!data.logradouro || !data.bairro) {
        setManualEntryEnabled(true);
      }

      setTimeout(() => numeroRef.current?.focus(), 100);
    } catch {
      // Erro na busca - habilitar campos imediatamente
      setCepNotFound(true);
      setManualEntryEnabled(true);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length === 8) {
      fetchEndereco(cepLimpo);
    }
  }, [cep]);

  const validateField = (field: string, value: string): string | undefined => {
    switch (field) {
      case 'cep':
        if (!value.trim()) return 'CEP é obrigatório';
        if (value.replace(/\D/g, '').length !== 8) return 'CEP deve ter 8 dígitos';
        break;
      case 'numero':
        if (!value.trim()) return 'Número é obrigatório';
        if (value.trim().length < 1) return 'Número é obrigatório';
        break;
    }
    return undefined;
  };

  const handleFieldChange = (field: string, value: string) => {
    const error = validateField(field, value);
    setFieldErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleFieldBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const value = field === 'cep' ? cep : address.numero;
    const error = validateField(field, value);
    setFieldErrors((prev) => ({ ...prev, [field]: error }));
  };

  const getFieldError = (field: string): string | undefined => {
    return touched[field as keyof typeof touched] ? fieldErrors[field as keyof typeof fieldErrors] : undefined;
  };

  const getFieldClassName = (field: string): string => {
    const hasError = getFieldError(field);
    const isTouched = touched[field as keyof typeof touched];
    const baseClass = 'border px-4 py-4 rounded-lg h-12';

    if (hasError && isTouched) {
      return `${baseClass} border-red-500 bg-red-50`;
    } else if (isTouched && !hasError) {
      return `${baseClass} border-green-500 bg-green-50`;
    }
    return `${baseClass} border-[#dadae5]`;
  };

  const isFormValid =
    cep.replace(/\D/g, '').length === 8 &&
    address.rua?.trim() &&
    address.bairro?.trim() &&
    address.cidade?.trim() &&
    address.estado?.trim() &&
    address.numero?.trim();

  const handleSave = async () => {
    // Validações mais específicas
    if (cep.replace(/\D/g, '').length !== 8) {
      alert('CEP deve ter 8 dígitos.');
      return;
    }

    if (!address.rua?.trim()) {
      alert('Rua é obrigatória.');
      return;
    }

    if (!address.bairro?.trim()) {
      alert('Bairro é obrigatório.');
      return;
    }

    if (!address.cidade?.trim()) {
      alert('Cidade é obrigatória.');
      return;
    }

    if (!address.estado?.trim()) {
      alert('Estado é obrigatório.');
      return;
    }

    if (!address.numero?.trim()) {
      alert('Número é obrigatório.');
      return;
    }

    try {
      setIsEditing(false);
      await onSubmitChildren({ cep, ...address });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (window.fbq) {
      window.fbq('track', 'AddPaymentInfo', {
        content_name: planoSelecionado?.name,
        value: planoSelecionado.price.toFixed(2),
        currency: 'BRL',
      });
    }
  }, []);

  return (
    <>
      <StepContainer>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <h1 className="font-bold text-xl mb-3">Endereço de entrega</h1>
          <span className="text-base text-[#595D69]">
            Digite o endereço de entrega para que possamos enviar os livros.
          </span>
        </motion.div>

        {!isEditing ? (
          <div className="bg-white border border-[#dadae5] rounded-lg p-4 mt-5 shadow-sm">
            <div className="flex items-center gap-4">
              <img src="/icon-maps.png" alt="" className="w-10 h-10" />
              <div>
                <p className="text-lg font-semibold text-[#333]">
                  {address.rua}, {address.numero}. {address.bairro}
                </p>
                <p className="text-sm text-[#666]">
                  {address.cidade} - {address.estado}. CEP: {cep}
                </p>
              </div>
            </div>
            <hr className="my-4 border-black/10" />
            <p className="text-sm text-[#444]">
              <strong>Recebedor:</strong> {data.name}
            </p>
          </div>
        ) : (
          <div className="mt-6 flex flex-col gap-4">
            <div>
              <label className="text-base text-[#595D69]">CEP</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="00000-000"
                  value={cep}
                  onChange={(e) => {
                    setCep(e.target.value);
                    handleFieldChange('cep', e.target.value);
                  }}
                  onBlur={() => handleFieldBlur('cep')}
                  className={getFieldClassName('cep') + ' w-full pr-10'}
                  aria-describedby={getFieldError('cep') ? 'cep-error' : undefined}
                  aria-invalid={!!getFieldError('cep')}
                />
                {getFieldError('cep') && (
                  <p id="cep-error" className="text-red-500 text-sm mt-1" role="alert">
                    {getFieldError('cep')}
                  </p>
                )}
                {loading && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <svg
                      className="animate-spin h-5 w-5 text-[#018ED5]"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                  </div>
                )}
              </div>
            </div>

            {/* Mensagem de CEP não encontrado */}
            {cepNotFound && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-yellow-800 text-sm">
                    CEP não encontrado. Por favor, preencha o endereço manualmente.
                  </p>
                </div>
              </div>
            )}

            {/* Campos de endereço - preenchimento automático ou manual */}
            {(address.rua || manualEntryEnabled) && (
              <>
                <div>
                  <label className="text-base text-[#595D69]">Rua</label>
                  <input
                    value={address.rua}
                    onChange={(e) => setAddress({ ...address, rua: e.target.value })}
                    readOnly={!manualEntryEnabled && !!address.rua}
                    className={`h-12 w-full border border-[#dadae5] px-4 py-4 rounded-lg ${
                      !manualEntryEnabled && address.rua ? 'bg-gray-50' : ''
                    }`}
                  />
                </div>

                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="text-base text-[#595D69]">Bairro</label>
                    <input
                      value={address.bairro}
                      onChange={(e) => setAddress({ ...address, bairro: e.target.value })}
                      readOnly={!manualEntryEnabled && !!address.bairro}
                      className={`h-12 w-full border border-[#dadae5] px-4 py-4 rounded-lg ${
                        !manualEntryEnabled && address.bairro ? 'bg-gray-50' : ''
                      }`}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-base text-[#595D69]">Cidade</label>
                    <input
                      value={address.cidade}
                      onChange={(e) => setAddress({ ...address, cidade: e.target.value })}
                      readOnly={!manualEntryEnabled && !!address.cidade}
                      className={`h-12 w-full border border-[#dadae5] px-4 py-4 rounded-lg ${
                        !manualEntryEnabled && address.cidade ? 'bg-gray-50' : ''
                      }`}
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-[100px]">
                    <label className="text-base text-[#595D69]">UF</label>
                    <input
                      value={address.estado}
                      onChange={(e) => setAddress({ ...address, estado: e.target.value })}
                      readOnly={!manualEntryEnabled && !!address.estado}
                      className={`h-12 w-full border border-[#dadae5] px-4 py-4 rounded-lg ${
                        !manualEntryEnabled && address.estado ? 'bg-gray-50' : ''
                      }`}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-base text-[#595D69]">Número</label>
                    <input
                      value={address.numero}
                      onChange={(e) => {
                        setAddress({ ...address, numero: e.target.value });
                        handleFieldChange('numero', e.target.value);
                      }}
                      onBlur={() => handleFieldBlur('numero')}
                      ref={numeroRef}
                      className={getFieldClassName('numero') + ' w-full'}
                      aria-describedby={getFieldError('numero') ? 'numero-error' : undefined}
                      aria-invalid={!!getFieldError('numero')}
                    />
                    {getFieldError('numero') && (
                      <p id="numero-error" className="text-red-500 text-sm mt-1" role="alert">
                        {getFieldError('numero')}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-base text-[#595D69]">Complemento</label>
                  <input
                    placeholder="Complemento (opcional)"
                    value={address.complemento}
                    onChange={(e) => setAddress({ ...address, complemento: e.target.value })}
                    className="h-12 w-full border border-[#dadae5] px-4 py-4 rounded-lg"
                  />
                </div>

                {/* <div>
                <label className="text-base text-[#595D69]">
                  Quem vai receber
                </label>
                <input
                  placeholder="Nome completo"
                  value={address.recebedor}
                  onChange={(e) =>
                    setAddress({ ...address, recebedor: e.target.value })
                  }
                  className="h-12 w-full border border-[#dadae5] px-4 py-4 rounded-lg"
                />
              </div> */}

                <button
                  onClick={handleSave}
                  disabled={!isFormValid || loading}
                  className={`mt-4 flex items-center justify-center gap-2 text-white px-6 py-2 rounded-md transition-all duration-300 ${
                    isFormValid && !loading ? 'bg-[#018ED5] hover:opacity-90' : 'bg-[#dadae5] cursor-not-allowed'
                  }`}
                >
                  {loading && (
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                  )}
                  {loading ? 'Salvando...' : 'Salvar endereço'}
                </button>
              </>
            )}
          </div>
        )}

        <div className="flex items-center justify-between mt-8">
          <button
            onClick={onBack}
            className="flex gap-2 hover:cursor-pointer items-center hover:opacity-90 text-[#9195A1] px-3 py-2 rounded-md"
          >
            <img src="/arrow-left.svg" alt="Voltar" /> Voltar
          </button>

          {!isEditing && (
            <div className="flex justify-between mt-4">
              <button
                onClick={() => setIsEditing(true)}
                className="bg-[#018ED5] text-white px-4 py-2 rounded hover:opacity-90"
              >
                Editar
              </button>
            </div>
          )}
        </div>
      </StepContainer>
    </>
  );
};

export default StepThree;
